import { isWorkflowFinished } from "../constants";
import type { GroupSummary, ProjectSummary, UserSession } from "../types";
import { escapeHtml, getProjectStatusLabel } from "../view-helpers";

function renderEmptyState(): string {
  return `
    <div class="panel empty-state">
      <h2>还没有项目</h2>
      <p class="muted">先创建一个项目，把项目壳子立起来。</p>
    </div>
  `;
}

function renderSyncLabel(project: ProjectSummary, pendingCount: number): string {
  if (pendingCount > 0) {
    return `待同步 ${pendingCount} 项`;
  }
  if (project.lastSyncedAt) {
    return `已同步 · ${new Date(project.lastSyncedAt).toLocaleString("zh-CN")}`;
  }
  return "未同步";
}

function buildProjectProgress(project: ProjectSummary): {
  finishedItems: number;
  totalItems: number;
  finishedSubitems: number;
  totalSubitems: number;
} {
  const finishedItems = project.inspectionItems.filter((item) => isWorkflowFinished(item.workflowStatus)).length;
  const totalItems = project.inspectionItems.length;
  const totalSubitems = project.inspectionItems.reduce((count, item) => count + item.subitems.length, 0);
  const finishedSubitems = project.inspectionItems.reduce(
    (count, item) => count + item.subitems.filter((subitem) => isWorkflowFinished(subitem.workflowStatus)).length,
    0,
  );

  return {
    finishedItems,
    totalItems,
    finishedSubitems,
    totalSubitems,
  };
}

function buildProjectListSummary(projects: ProjectSummary[]): {
  totalProjects: number;
  readyProjects: number;
  activeProjects: number;
} {
  const totalProjects = projects.length;
  const readyProjects = projects.filter((project) => project.inspectionItems.every((item) => isWorkflowFinished(item.workflowStatus))).length;
  const activeProjects = totalProjects - readyProjects;

  return {
    totalProjects,
    readyProjects,
    activeProjects,
  };
}

function renderGroupSelect(groups: GroupSummary[], targetInputId: string): string {
  if (!groups.length) {
    return "";
  }
  return `
    <label class="field">
      <span>已有小组</span>
      <select class="select-field" data-action="copy-group-option" data-target-input="${targetInputId}">
        <option value="">从已有小组中选择</option>
        ${groups
          .map(
            (group) => `
              <option value="${escapeHtml(group.name)}">${escapeHtml(group.name)}</option>
            `,
          )
          .join("")}
      </select>
    </label>
  `;
}

function renderProjectCard(project: ProjectSummary, pendingCount: number, isAdmin: boolean, groups: GroupSummary[]): string {
  const progress = buildProjectProgress(project);
  const inputId = `project-group-${project.id}`;

  return `
    <div class="panel project-card">
      <div class="project-card-head">
        <strong>${escapeHtml(project.name)}</strong>
        <span class="status-pill">${getProjectStatusLabel(project.status)}</span>
      </div>
      <p class="muted">所属小组：${escapeHtml(project.groupName || "未分组")}</p>
      <p class="muted">创建人：${escapeHtml(project.creatorUserName || "未记录")}</p>
      <p class="muted">最近更新：${new Date(project.updatedAt).toLocaleString("zh-CN")}</p>
      <p class="muted">同步状态：${escapeHtml(renderSyncLabel(project, pendingCount))}</p>
      <div class="inline-pills">
        <span class="state-pill">单项 ${progress.finishedItems}/${progress.totalItems}</span>
        <span class="state-pill">子项 ${progress.finishedSubitems}/${progress.totalSubitems}</span>
      </div>
      ${
        isAdmin
          ? `
            <div class="panel nested-panel stack compact-stack">
              <div class="field">
                <span>调整项目分组</span>
                <input
                  id="${inputId}"
                  type="text"
                  value="${escapeHtml(project.groupName || "")}"
                  placeholder="输入新的项目分组"
                  maxlength="24"
                  autocomplete="off"
                  data-project-group-input="${project.id}"
                />
              </div>
              ${renderGroupSelect(groups, inputId)}
              <button
                class="ghost-button small-button"
                type="button"
                data-action="save-project-group"
                data-project-id="${project.id}"
                data-project-name="${escapeHtml(project.name)}"
              >
                保存项目分组
              </button>
            </div>
          `
          : ""
      }
      <div class="action-row">
        <button class="primary-button" type="button" data-action="open-project" data-project-id="${project.id}">进入项目</button>
        <button class="ghost-button small-button" type="button" data-action="delete-project" data-project-id="${project.id}" data-project-name="${escapeHtml(project.name)}">删除项目</button>
      </div>
    </div>
  `;
}

export function renderProjectListPage(
  user: UserSession,
  projects: ProjectSummary[],
  pendingCountByProject: Record<string, number>,
  groups: GroupSummary[],
): string {
  const isAdmin = user.role === "admin";
  const summary = buildProjectListSummary(projects);

  return `
    <section class="screen">
      <header class="topbar">
        <div>
          <p class="eyebrow">欢迎回来</p>
          <h1>${escapeHtml(user.name)}</h1>
          <p class="muted">${isAdmin ? "当前为管理员账号，可查看全部项目并管理用户。" : `当前账号所属小组：${escapeHtml(user.groupName)}，仅可查看和编辑本组项目。`}</p>
        </div>
        <button class="ghost-button" data-action="logout">退出</button>
      </header>
      <div class="panel stack">
        <div class="inline-head">
          <div>
            <h2>项目列表</h2>
            <p class="muted">${isAdmin ? "管理员可查看全部项目；普通用户只能查看本组项目。" : "同组成员可共同查看、编辑、同步、生成和发送本组项目。"}</p>
          </div>
          <div class="action-row wrap-actions">
            ${isAdmin ? `<button class="ghost-button" data-action="open-admin-users">用户管理</button>` : ""}
            <button class="primary-button" data-action="create-project">新建项目</button>
          </div>
        </div>
        <div class="inline-pills">
          <span class="state-pill">共 ${summary.totalProjects} 个项目</span>
          <span class="state-pill">${summary.activeProjects} 个进行中</span>
          <span class="state-pill">${summary.readyProjects} 个已完成现场评定</span>
        </div>
        ${projects.length ? projects.map((project) => renderProjectCard(project, pendingCountByProject[project.id] || 0, isAdmin, groups)).join("") : renderEmptyState()}
      </div>
    </section>
  `;
}
